import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "rootRef", "value", "defaultValue", "type", "style", "prefix", "suffix", "error", "success", "disabled", "children", "onChange"];
import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import InputStyleBase from '../InputBase';
import { isFunction } from '../helper';
import { useMergeRefs } from '../hooks';
import Affix from './Affix';
import { useFormControl } from '../Form';
import InputControl from './InputControl';
const InputBase = styled(InputStyleBase).withConfig({
  displayName: "Input__InputBase",
  componentId: "sc-2oktpa-0"
})(["min-height:24px;box-shadow:none;", ""], _ref => {
  let {
    disabled
  } = _ref;
  return disabled && css(["background:var(--neutral-surface-disabled);color:var(--neutral-text-disabled);"]);
});
const Input = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
      className,
      rootRef,
      value,
      defaultValue,
      type,
      style,
      prefix,
      suffix,
      error,
      success,
      disabled,
      onChange
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
  const inputRef = useRef();
  const {
    required: requiredFormControl,
    error: errorFormControl,
    success: successFormControl
  } = useFormControl();
  const required = requiredFormControl || rest.required;
  const isSuccess = successFormControl || success;
  const isError = errorFormControl || error;
  const handleChange = event => {
    isFunction(onChange) && onChange(event);
  };
  const handleClick = () => {
    inputRef.current.focus();
  };
  const {
    prefix: PREFIX,
    suffix: SUFFIX
  } = Affix.categories;
  return /*#__PURE__*/React.createElement(InputControl, {
    ref: rootRef,
    className: className,
    style: style,
    error: isError,
    success: isSuccess,
    disabled: disabled,
    onClick: handleClick
  }, prefix && /*#__PURE__*/React.createElement(Affix, {
    category: PREFIX
  }, prefix), /*#__PURE__*/React.createElement(InputBase, _extends({}, rest, {
    "aria-required": required,
    ref: useMergeRefs([inputRef, ref]),
    disabled: disabled,
    value: value,
    defaultValue: defaultValue,
    type: type,
    "aria-disabled": disabled,
    "aria-invalid": isError,
    onChange: handleChange
  })), suffix && /*#__PURE__*/React.createElement(Affix, {
    category: SUFFIX
  }, suffix));
});
Input.displayName = 'Input';
Input.defaultProps = {
  prefix: null,
  suffix: null,
  error: false,
  success: false,
  disabled: false
};
process.env.NODE_ENV !== "production" ? Input.propTypes = {
  /**
   * When the value is `true`, makes the element not mutable, focusable, or even submitted with the form.
   * The user can neither edit nor focus on the control.
   */
  disabled: PropTypes.bool,
  /**
   * When the value is `true`, changes the layout to demonstrate that an error has occurred.
   */
  error: PropTypes.bool,
  /**
   * When the value is `true`, changes the layout to demonstrate that something positive has happened.
   */
  success: PropTypes.bool,
  /**
   * Text that appears in the form control when it has no value set.
   */
  placeholder: PropTypes.string,
  /**
   * Adds content as a prefix to the value.
   */
  prefix: PropTypes.node,
  /**
   * Adds content as a suffix to the value.
   */
  suffix: PropTypes.node,
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue: PropTypes.any,
  /**
   * The value of the input element, required for a controlled component.
   */
  value: PropTypes.any,
  /**
   * Callback fired when the value is changed.
   *
   * @param {React.SyntheticEvent} event - The event source of the callback.
   *
   * @example
   * <Input
   *   name="example"
   *   onChange={function (event) {
   *     const { name, value } = event.target;
   *     console.log(`The input's name is ${name} and the value is ${value}`);
   *   }}
   * />
   */
  onChange: PropTypes.func
} : void 0;
export default Input;